import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SEO from "@/components/SEO";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success("Welcome back!");
      nav(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO title="Login | NewStarAds" description="Log in to your NewStarAds account." />
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl"><span className="grid place-items-center w-8 h-8 rounded-lg bg-primary"><Youtube className="w-5 h-5" /></span> NewStarAds</Link>
        <div><h2 className="font-display text-4xl font-bold tracking-tight leading-tight">Transparent YouTube marketing, managed professionally.</h2><p className="mt-4 text-slate-400">Access your campaigns, reports and account.</p></div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} NewStarAds</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div><h1 className="font-display text-3xl font-bold tracking-tight">Log in</h1><p className="mt-1 text-slate-500 text-sm">Welcome back to NewStarAds.</p></div>
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
          <div className="text-right -mt-2"><Link to="/forgot-password" className="text-sm text-primary font-medium" data-testid="login-forgot">Forgot password?</Link></div>
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full" data-testid="login-submit">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log in"}</Button>
          <div className="flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-200" /> or <span className="flex-1 h-px bg-slate-200" /></div>
          <GoogleSignInButton />
          <p className="text-sm text-slate-500 text-center">No account? <Link to="/register" className="text-primary font-medium">Create one</Link></p>
        </form>
      </div>
    </div>
  );
}
